import { format, formatISO } from "date-fns";
import React from "react";
import { useStore } from "zustand";

import type { DateComponentProps } from "../../components/common/Date";
import { DateComponent } from "../../components/common/Date";
import {
    useGetDimensionDisable,
    useSetDimensionValue,
    useUpdateStatusOnChange,
    useClearDimensionValueOnDependencyChange,
} from "../../hooks/dimension/dimension.hooks";
import { alertStore } from "../../store/alert";
import { useDimensionHydration } from "../../store/dimension.hydration";
import { DimensionStatus } from "../../store/dimension.status";
import type { DateDimensionValue } from "../../store/dimension.value";
import { dimensionValueStore } from "../../store/dimension.value";
import type {
    DimensionConfiguration,
    DimensionTemplate,
} from "../../types/dimensions";
import { isValidDateString } from "../../utils/date.utils";

import type { DateOption } from "./DateDimensionHelpers";
import {
    DateType,
    getAbsoluteDate,
    getDimensionIdDate,
    getRelativeDate,
} from "./DateDimensionHelpers";
import { BasicDimensionBox } from "./common/components";

export interface DateDimensionConfiguration extends DimensionConfiguration {
    dateFormat?: string;
    startRange?: string;
    additionalFormats?: Record<string, string>;
    startDate?: DateOption;
    endDate?: DateOption;
}

export interface DateDimensionProps extends DimensionTemplate {
    configuration: DateDimensionConfiguration;
}

export const DateDimension: React.FunctionComponent<DateDimensionProps> = (
    props,
) => {
    const {
        id,
        dependencies,
        configuration,
        optional = false,
        requestParameterMap,
    } = props;

    const {
        label,
        dateFormat,
        startRange,
        startDate,
        endDate,
        tooltip,
        additionalFormats = {},
    } = configuration;

    if (startRange && startDate) {
        throw new Error(`startRange and startDate can not be both defined`);
    }

    const disabled = useGetDimensionDisable(dependencies);

    const [setAlert] = useStore(alertStore, (state) => [state.setAlert]);

    const [dimensionData, setDimensionData] = useSetDimensionValue(id, (v) => {
        let formattedDate = "";
        try {
            formattedDate = dateFormat
                ? format(new Date(v.value), dateFormat)
                : formatISO(new Date(v.value));
            Object.keys(additionalFormats).forEach((key: string) => {
                additionalFormats[key] = format(
                    new Date(v.value),
                    additionalFormats[key],
                );
            });
        } catch (err: any) {
            setAlert(`Invalid date format: ${err.message}`, "error", err.stack);
        }
        return {
            [requestParameterMap?.value || id]: formattedDate,
            ...additionalFormats,
        };
    });

    const [startRangeDate] = useStore(dimensionValueStore, (state) => [
        startRange && isValidDateString(startRange)
            ? startRange
            : state.getDimensionData(startRange!).value,
    ]);

    const [getDimensionData] = useStore(dimensionValueStore, (state) => [
        state.getDimensionData,
    ]);

    useUpdateStatusOnChange<DateDimensionValue>(id, ({ value }) => {
        if (optional) return DimensionStatus.SUCCESS;
        if (value.length === 0) {
            return DimensionStatus.EMPTY;
        }
        return DimensionStatus.SUCCESS;
    });

    useClearDimensionValueOnDependencyChange(id, dependencies);

    const getDateOptionValue = (dateOption?: DateOption) => {
        if (!dateOption) return undefined;
        switch (dateOption.type) {
            case DateType.DIMENSION_ID:
                return getDimensionIdDate(getDimensionData, dateOption.id);
            case DateType.ABSOLUTE:
                return getAbsoluteDate(dateOption.dateString);
            case DateType.RELATIVE:
                return getRelativeDate(dateOption.daysRelatativeToToday);
            default:
                return undefined;
        }
    };

    useDimensionHydration(id, dependencies, async (hydrationPayload) => {
        const payloadValues = {
            valueId: (() => {
                if (requestParameterMap) {
                    return requestParameterMap.value;
                } else {
                    return id;
                }
            })(),
        };

        if (hydrationPayload[payloadValues.valueId]) {
            const value = hydrationPayload[payloadValues.valueId];
            if (
                getDateOptionValue(startDate) &&
                new Date(value) < getDateOptionValue(startDate)!
            ) {
                throw new Error("date is not in the allowed time range");
            }
            if (
                getDateOptionValue(endDate) &&
                new Date(value) > getDateOptionValue(endDate)!
            ) {
                throw new Error("date is not in the allowed time range");
            }
            setDimensionData({ value });
        } else {
            throw new Error("could not locate value in payload");
        }
    });

    const dateComponentProps: DateComponentProps = {
        dimension: id,
        value: dimensionData.value,
        setValue: (value) => setDimensionData({ value } as DateDimensionValue),
        disabled,
        label,
        startDate: startRangeDate
            ? new Date(startRangeDate)
            : getDateOptionValue(startDate),
        endDate: getDateOptionValue(endDate),
        optional,
        tooltip,
    };

    return (
        <BasicDimensionBox>
            <DateComponent {...dateComponentProps} />
        </BasicDimensionBox>
    );
};
